import * as React from "react"
import { cn } from "@/lib/utils"

interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  /** Prioridade alta para LCP (ex: primeiros cards da home) */
  priority?: boolean
  /** Alt é obrigatório para acessibilidade */
  alt: string
  /** Mostrar placeholder enquanto carrega (skeleton) */
  showPlaceholder?: boolean
}

/**
 * Imagem otimizada com lazy loading, decoding async e placeholder.
 * - priority: true → carrega imediatamente (LCP)
 * - priority: false → loading="lazy", decoding="async"
 * - showPlaceholder: exibe skeleton enquanto a imagem carrega
 */
function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (node: T | null) => {
    for (const ref of refs) {
      if (!ref) continue
      if (typeof ref === "function") ref(node)
      else (ref as React.MutableRefObject<T | null>).current = node
    }
  }
}

export const OptimizedImage = React.forwardRef<HTMLImageElement, OptimizedImageProps>(
  (
    {
      priority = false,
      loading,
      decoding,
      fetchPriority,
      className,
      showPlaceholder = false,
      src,
      onLoad,
      ...props
    },
    ref,
  ) => {
    const [loaded, setLoaded] = React.useState(false)
    const internalRef = React.useRef<HTMLImageElement>(null)
    const effectiveLoading = loading ?? (priority ? "eager" : "lazy")
    const effectiveDecoding = decoding ?? "async"
    const effectiveFetchPriority = fetchPriority ?? (priority ? "high" : undefined)

    React.useLayoutEffect(() => {
      setLoaded(false)
      const img = internalRef.current
      if (img?.complete && img.naturalWidth > 0) {
        setLoaded(true)
      }
    }, [src])

    const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
      setLoaded(true)
      onLoad?.(e)
    }

    const imgEl = (
      <img
        ref={mergeRefs(ref, internalRef)}
        src={src}
        loading={effectiveLoading}
        decoding={effectiveDecoding}
        fetchPriority={effectiveFetchPriority}
        className={cn(
          className,
          showPlaceholder && "absolute inset-0 h-full w-full",
          showPlaceholder && !loaded && "opacity-0")}
        onLoad={handleLoad}
        {...props}
      />
    )

    if (showPlaceholder) {
      return (
        <span className="relative block size-full overflow-hidden bg-muted dark:bg-white/10">
          {!loaded && <span className="absolute inset-0 bg-muted dark:bg-white/10" aria-hidden />}
          {imgEl}
        </span>
      )
    }

    return imgEl
  },
)
OptimizedImage.displayName = "OptimizedImage"
